import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Save, Loader2, Upload, QrCode } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { qrSrc } from "@/lib/bank";

const inputCls = "w-full border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#6D28D9]/60";
const FIELDS = [
  ["account_name", "Account holder name", "Sanjay Jaysingh Thakur"],
  ["bank_name", "Bank name", "HDFC Bank"],
  ["account_number", "Account number", "50100288519679"],
  ["ifsc", "IFSC code", "HDFC0000411"],
  ["upi_id", "UPI ID", "name@okaxis"],
];

export const BankDetailsTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/payments/bank-details").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { account_name, bank_name, account_number, ifsc, upi_id } = form;
      const res = await api.put("/admin/settings/bank-details", { account_name, bank_name, account_number, ifsc, upi_id });
      setForm(res.data);
      toast.success("Bank details updated — new orders will show these details.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const uploadQr = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/settings/bank-details/qr", fd);
      setForm(res.data);
      toast.success("UPI QR code updated.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setUploading(false); }
  };

  if (!form) return <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">Loading bank details…</p>;

  return (
    <div data-testid="admin-bank-tab">
      <div>
        <h2 className="font-display text-2xl font-semibold tracking-tight">Bank Details</h2>
        <p className="mt-1 text-sm text-slate-500">The account and UPI QR investors see when paying by bank transfer. Changes apply to every new order instantly.</p>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_280px]">
        <form data-testid="admin-bank-form" onSubmit={save} className="grid gap-5 border border-slate-200 bg-[#F6F4FD]/70 p-6 md:grid-cols-2">
          {FIELDS.map(([k, label, ph]) => (
            <label key={k} className={k === "upi_id" ? "block md:col-span-2" : "block"}>
              <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
              <input data-testid={`admin-bank-${k.replace("_", "-")}`} required value={form[k] || ""} onChange={set(k)} placeholder={ph} className={`${inputCls} mt-1.5 ${k === "account_number" || k === "ifsc" ? "font-mono2" : ""}`} />
            </label>
          ))}
          <div className="md:col-span-2 flex items-center justify-between">
            <p className="font-mono2 text-[9px] uppercase tracking-[0.15em] text-slate-400">{form.updated_at ? `Last changed ${new Date(form.updated_at).toLocaleString("en-IN")}` : "Using default account"}</p>
            <button data-testid="admin-bank-save" type="submit" disabled={saving} className="flex items-center gap-2 bg-[#6D28D9] px-6 py-3 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#5B21B6] disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5" /> Save bank details</>}
            </button>
          </div>
        </form>

        <div className="flex flex-col items-center border border-slate-200 bg-white p-5">
          <p className="flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500"><QrCode className="h-3.5 w-3.5" /> UPI QR code</p>
          <img data-testid="admin-bank-qr-preview" src={qrSrc(form.upi_qr)} alt="UPI QR" className="mt-3 w-48 border border-slate-200 p-1" />
          <label data-testid="admin-bank-qr-upload" className="mt-4 flex cursor-pointer items-center gap-2 border border-[#6D28D9]/40 px-4 py-2.5 font-mono2 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6D28D9] transition-colors hover:bg-violet-50">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Replace QR image
            <input data-testid="admin-bank-qr-input" type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadQr(e.target.files?.[0])} />
          </label>
          <p className="mt-3 text-center text-[11px] text-slate-400">PNG/JPG, ≤2 MB. Export it from your UPI app.</p>
        </div>
      </div>
      <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Landmark className="h-3.5 w-3.5 text-[#6D28D9]" /> Investors copy these details from the "Pay by bank transfer / UPI" panel on every company page.</p>
    </div>
  );
};
